import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Input } from "components/base/Form";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { FETCH_PRICE_LISTS } from "../priceLists/graphql";
import { ADD_PRODUCTS_TO_PRICE_LISTS } from "./graphql";

const AddToPriceListsView = ({ products }) => {
  const { loading, error, data } = useQuery(FETCH_PRICE_LISTS);
  const [save, saveRes] = useMutation(ADD_PRODUCTS_TO_PRICE_LISTS, {
    onCompleted() {
      Alert("success", "Products added to price lists successfully");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const [state, setState] = useState(
    products.map((i) => ({
      ...i,
      price: "",
    })),
  );
  const [selectedPriceListIds, setSelectedPriceListIds] = useState([]);

  function onChangePrice(productId, newPrice) {
    setState((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, price: newPrice } : p)),
    );
  }

  function addHandler() {
    save({
      variables: {
        priceListIds: selectedPriceListIds,
        prices: state.map((p) => ({
          productId: p.id,
          price: parseFloat(p.price),
          discountPercentage: 0,
        })),
      },
    });
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="m-9 mt-4 text-xs h-full flex flex-col overflow-auto space-y-6">
      <div className="card p-4 py-2">
        <table>
          <thead>
            <tr>
              <th>Products</th>
              <th className="text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            {state.map((product) => (
              <tr key={product.id}>
                <td className="py-1">
                  [{product.number}] {product.name}
                </td>
                <td className="text-right py-1 pr-1">
                  <Input
                    className="text-right"
                    type="number"
                    placeholder="Price"
                    value={product.price}
                    onChange={(e) => {
                      onChangePrice(product.id, e.target.value);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card p-0 flex-1 overflow-auto">
        <table>
          <thead>
            <tr className="sticky top-0 bg-gray-50 bg-opacity-70 backdrop-blur z-10">
              <th className="py-3 px-6">Price Lists</th>
              <th className="text-right space-x-2 px-6">
                {selectedPriceListIds.length > 0 && (
                  <span className="font-normal opacity-70">
                    {selectedPriceListIds.length} price list
                    {selectedPriceListIds.length > 1 ? "s" : ""} selected
                  </span>
                )}
                <label htmlFor="toggle-all">Toggle All</label>
                <input
                  type="checkbox"
                  id="toggle-all"
                  checked={
                    selectedPriceListIds.length === data.priceLists.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPriceListIds(
                        data.priceLists.map((pl) => pl.id),
                      );
                    } else {
                      setSelectedPriceListIds([]);
                    }
                  }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {data.priceLists.map((pl) => (
              <tr
                key={pl.id}
                className="border-y border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900"
                onClick={() => {
                  if (selectedPriceListIds.includes(pl.id)) {
                    setSelectedPriceListIds(
                      selectedPriceListIds.filter((id) => id !== pl.id),
                    );
                  } else {
                    setSelectedPriceListIds([...selectedPriceListIds, pl.id]);
                  }
                }}
              >
                <td className="px-6">
                  {pl.name}({pl.currency})
                </td>
                <td className="text-right px-6">
                  <input
                    type="checkbox"
                    checked={selectedPriceListIds.includes(pl.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPriceListIds([
                          ...selectedPriceListIds,
                          pl.id,
                        ]);
                      } else {
                        setSelectedPriceListIds(
                          selectedPriceListIds.filter((id) => id !== pl.id),
                        );
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <Button primary onClick={addHandler} loading={saveRes.loading}>
          Add to Selected Price List(s)
        </Button>
      </div>
    </div>
  );
};

export default AddToPriceListsView;
